import { useId } from "react";
import { useTranslation } from "react-i18next";
import type { ContentVersion } from "../../../api/content/workflowsApi";
import { formatTimeAgo } from "../../../shared/formatting/relativeTimes";
import EmptyState from "../../../ui/feedback/EmptyState";
import Field from "../../../ui/forms/Field";
import OptionLabel from "../../../ui/forms/OptionLabel";
import { FORM_COMPACT_CONTROL_CLASS, FORM_SELECT_CLASS } from "../../../ui/styles/formStyles";
import { versionName } from "../../ontology/modelVersions";
import type { AcceptedChoice } from "../datasets/acceptedResults";
import AcceptedResultsPicker from "./AcceptedResultsPicker";
import type { ReleaseDraft } from "./releaseRequest";
import { processTerm } from "../../../api/content/processTerms";
import { classText } from "../../workflows/inputCards/typeWords";

type DraftChange = (change: Partial<ReleaseDraft>) => void;

/** Step 1: the content version whose data model the release is checked against, and the accepted results. */
export function WhatStep({
  draft,
  onChange,
  versions,
  choices,
}: {
  draft: ReleaseDraft;
  onChange: DraftChange;
  versions: readonly ContentVersion[];
  choices: readonly AcceptedChoice[];
}) {
  const { t, i18n } = useTranslation();
  const headingId = useId();
  if (versions.length === 0) {
    return <EmptyState size="small">{t("data.release.noModelVersion")}</EmptyState>;
  }
  return (
    <div className="space-y-4">
      <Field label={classText(processTerm("ContentVersion"), t)} hint={t("data.release.modelVersionHint")}>
        {(control) => (
          <select
            {...control}
            className={FORM_SELECT_CLASS}
            value={draft.modelVersion ?? ""}
            onChange={(event) => { onChange({ modelVersion: event.target.value }); }}
          >
            {versions.map((version, index) => (
              <option key={version.commit} value={version.commit}>
                {t(index === 0 ? "data.release.newestVersion" : "data.release.olderVersion", { name: versionName(version.message), time: formatTimeAgo(version.committedAt, i18n.resolvedLanguage) })}
              </option>
            ))}
          </select>
        )}
      </Field>
      <section className="space-y-1" aria-labelledby={headingId}>
        <h4 id={headingId} className="app-subsection-title text-sm">{t("data.release.results")}</h4>
        <AcceptedResultsPicker choices={choices} overrides={draft.overrides} onChange={(overrides) => { onChange({ overrides }); }} />
      </section>
    </div>
  );
}

/** Step 2: title, licence and description, taken from the last release. */
export function MetadataStep({ draft, onChange, firstRelease }: { draft: ReleaseDraft; onChange: DraftChange; firstRelease: boolean }) {
  const { t } = useTranslation();
  return (
    <div className="space-y-3">
      <p className="app-text-muted text-sm">{firstRelease ? t("data.release.metadataFirst") : t("data.release.metadataFromLast")}</p>
      <Field label={t("data.dataset.fields.title")}>
        {(control) => (
          <input {...control} className={FORM_COMPACT_CONTROL_CLASS} value={draft.title} required maxLength={200} onChange={(event) => { onChange({ title: event.target.value }); }} />
        )}
      </Field>
      <Field label={t("data.dataset.fields.licence")} hint={t("data.dataset.fields.licenceHelp")}>
        {(control) => (
          <input {...control} className={FORM_COMPACT_CONTROL_CLASS} value={draft.licence} required onChange={(event) => { onChange({ licence: event.target.value }); }} />
        )}
      </Field>
      <Field label={t("data.dataset.fields.description")}>
        {(control) => (
          <textarea {...control} className={FORM_COMPACT_CONTROL_CLASS} rows={3} value={draft.description} onChange={(event) => { onChange({ description: event.target.value }); }} />
        )}
      </Field>
    </div>
  );
}

/** Step 3: ResearchSpace, the one destination; the file download comes with every release. */
export function WhereStep({ draft, onChange, available, canPublish }: { draft: ReleaseDraft; onChange: DraftChange; available: boolean; canPublish: boolean }) {
  const { t } = useTranslation();
  const reason = canPublish ? null : t(available ? "data.release.projectOff" : "data.release.noResearchSpace");
  return (
    <div className="space-y-4">
      <div className="space-y-1">
        <OptionLabel align="start">
          <input
            type="checkbox"
            checked={reason === null && draft.toResearchSpace}
            disabled={reason !== null}
            onChange={(event) => { onChange({ toResearchSpace: event.target.checked }); }}
          />
          <span>
            <span className="block font-semibold">{t("data.release.toResearchSpace")}</span>
            {reason === null ? null : <span className="app-text-muted block text-xs">{reason}</span>}
          </span>
        </OptionLabel>
      </div>
      <p className="app-text-muted text-sm">{t("data.release.downloadAlways")}</p>
    </div>
  );
}
